/**
 * Theme: Mensure Dark
 *
 * Tema escuro inspirado no Google Dark Theme
 * Usando a mesma paleta de cores do tema escuro do Google
 */

import { shadows, shapes } from '../tokens';
import typographyTokens from '../tokens/typography';

// Cores do Mensure Dark Theme - Chumbo
const googleDarkColors = {
  // Background principal - cinza muito escuro
  backgroundDefault: '#202124',

  // Background de cards/paper - cinza escuro levemente mais claro
  backgroundPaper: '#292a2d',

  // Cor primária - branco
  primary: '#ffffff',

  // Cor secundária - cinza claro
  secondary: '#e0e0e0',

  // Textos
  textPrimary: '#e8eaed',      // Branco/cinza muito claro
  textSecondary: '#9aa0a6',    // Cinza médio
  textDisabled: '#5f6368',     // Cinza escuro

  // Divider
  divider: '#3c4043',          // Cinza escuro

  // Estados de ação
  actionActive: 'rgba(232, 234, 237, 0.7)',
  actionHover: 'rgba(232, 234, 237, 0.08)',
  actionSelected: 'rgba(255, 255, 255, 0.16)',
  actionDisabled: 'rgba(232, 234, 237, 0.3)',
  actionDisabledBg: 'rgba(232, 234, 237, 0.12)',

  // Cores de sucesso, erro, warning, info - Google style
  success: '#81c995',
  error: '#f28b82',
  warning: '#ff9800',
  info: '#78d9ec'
};

const mensureDarkTheme = {
  id: 'escuro',
  name: 'Escuro',
  palette: {
    mode: 'dark',
    primary: {
      main: googleDarkColors.primary,
      light: '#ffffff',
      dark: '#cccccc',
      contrastText: '#000000'
    },
    secondary: {
      main: googleDarkColors.secondary,
      light: '#f5f5f5',
      dark: '#bdbdbd',
      contrastText: '#000000'
    },
    background: {
      default: googleDarkColors.backgroundDefault,
      paper: googleDarkColors.backgroundPaper
    },
    text: {
      primary: googleDarkColors.textPrimary,
      secondary: googleDarkColors.textSecondary,
      disabled: googleDarkColors.textDisabled
    },
    divider: googleDarkColors.divider,
    action: {
      active: googleDarkColors.actionActive,
      hover: googleDarkColors.actionHover,
      selected: googleDarkColors.actionSelected,
      disabled: googleDarkColors.actionDisabled,
      disabledBackground: googleDarkColors.actionDisabledBg
    },
    success: {
      main: googleDarkColors.success
    },
    error: {
      main: '#6F0023',
      light: '#9A1A3D',
      dark: '#4D0019'
    },
    warning: {
      main: googleDarkColors.warning
    },
    info: {
      main: googleDarkColors.info
    }
  },

  shape: {
    borderRadius: 8  // Google usa cantos arredondados sutis
  },
  typography: typographyTokens.typography.light, // Usando a mesma base tipográfica refinada

  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)'
  ],

  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: googleDarkColors.backgroundPaper,
          color: googleDarkColors.textPrimary,
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
          borderBottom: `1px solid ${googleDarkColors.divider}`
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: googleDarkColors.backgroundPaper,
          borderRight: `1px solid ${googleDarkColors.divider}`
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',  // Google não usa uppercase
          fontWeight: 500
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)'
          }
        },
        outlined: {
          borderColor: googleDarkColors.divider,
          '&:hover': {
            borderColor: googleDarkColors.primary,
            backgroundColor: 'rgba(255, 255, 255, 0.08)'
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
          borderRadius: 8,
          border: `1px solid ${googleDarkColors.divider}`
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: googleDarkColors.backgroundPaper,
          backgroundImage: 'none'  // Google não usa gradientes
        },
        elevation1: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)'
        },
        elevation2: {
          boxShadow: '0 2px 6px 2px rgba(0, 0, 0, 0.15)'
        },
        elevation3: {
          boxShadow: '0 4px 8px 3px rgba(0, 0, 0, 0.15)'
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small'
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: googleDarkColors.divider,
              borderRadius: 8
            },
            '&:hover fieldset': {
              borderColor: googleDarkColors.textSecondary
            },
            '&.Mui-focused fieldset': {
              borderColor: googleDarkColors.primary
            }
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: 8
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,  // Chips arredondados estilo Google
        },
        sizeSmall: {
          height: 24,
          fontSize: '0.75rem',
          '& .MuiSvgIcon-root': {
            fontSize: '1rem'
          }
        },
        outlined: {
          borderColor: googleDarkColors.divider
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: googleDarkColors.primary,
            '& + .MuiSwitch-track': {
              backgroundColor: googleDarkColors.primary,
              opacity: 0.5
            }
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: googleDarkColors.actionHover
          }
        },
        sizeSmall: {
          padding: '6px' // Evitar que fique amassado demais
        }
      }
    },
    // Correções para cor primária branca
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: googleDarkColors.textPrimary
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: googleDarkColors.textPrimary
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: googleDarkColors.textPrimary, // Garantir que todos os Typography usem cor clara
          '&.MuiTypography-colorPrimary': {
            color: googleDarkColors.textPrimary
          }
        },
        h1: { color: googleDarkColors.textPrimary },
        h2: { color: googleDarkColors.textPrimary },
        h3: { color: googleDarkColors.textPrimary },
        h4: { color: googleDarkColors.textPrimary },
        h5: { color: googleDarkColors.textPrimary },
        h6: { color: googleDarkColors.textPrimary },
        body1: { color: googleDarkColors.textPrimary },
        body2: { color: googleDarkColors.textSecondary }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            '& .MuiInputAdornment-root': {
              color: googleDarkColors.textPrimary
            }
          }
        },
        input: {
          '&::selection': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            color: googleDarkColors.textPrimary
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: googleDarkColors.textSecondary
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: googleDarkColors.textPrimary
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem'
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: googleDarkColors.textPrimary
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem'
          }
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        fontSizeSmall: {
          fontSize: '1.2rem' // Garante que nenhum SVG pequeno fique minúsculo (menor que ~19px)
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36, // Deixa os menus mais compactos mas os ícones legíveis
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem'
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500,
        },
        // Warning - fundo escuro com borda laranja sutil e texto claro
        standardWarning: {
          backgroundColor: 'rgba(255, 152, 0, 0.12)',
          color: googleDarkColors.textPrimary,
          border: `1px solid rgba(255, 152, 0, 0.25)`,
          '& .MuiAlert-icon': {
            color: googleDarkColors.warning
          }
        },
        // Error - fundo escuro com borda vermelha sutil
        standardError: {
          backgroundColor: 'rgba(242, 139, 130, 0.12)',
          color: googleDarkColors.textPrimary,
          border: `1px solid rgba(242, 139, 130, 0.25)`,
          '& .MuiAlert-icon': {
            color: googleDarkColors.error
          }
        },
        // Success - fundo escuro com borda verde sutil
        standardSuccess: {
          backgroundColor: 'rgba(129, 201, 149, 0.12)',
          color: googleDarkColors.textPrimary,
          border: `1px solid rgba(129, 201, 149, 0.25)`,
          '& .MuiAlert-icon': {
            color: googleDarkColors.success
          }
        },
        // Info - fundo escuro com borda azul sutil
        standardInfo: {
          backgroundColor: 'rgba(120, 217, 236, 0.12)',
          color: googleDarkColors.textPrimary,
          border: `1px solid rgba(120, 217, 236, 0.25)`,
          '& .MuiAlert-icon': {
            color: googleDarkColors.info
          }
        },
        filledWarning: {
          backgroundColor: googleDarkColors.warning,
          color: '#000',
          fontWeight: 600
        }
      }
    }
  }
};

export default mensureDarkTheme;
